import moment from "moment";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CommentActionType } from "@/Common/Models/MedicalRequest/Enum/CommentActionType";
import { Comment } from "@/Common/Models/Patient/Comment";

import styles from "./MedicalRequestPopupController.module.scss";

interface IMedicalRequestCommentsDisplayProps {
    comments: Comment[];
    showLastItemBorder?: boolean;
    label?: string;
}

function MedicalRequestCommentsDisplay(props: Readonly<IMedicalRequestCommentsDisplayProps>): JSX.Element {
    const { getText } = useTranslation();

    if (!props?.comments?.length)
        return null;

    const getCommentTitle = (comment: Readonly<Comment>): string => {
        if (!comment.CreateDate) {
            return "";
        }

        const dateFormat = "YYYY-MM-DD";
        return `${getText("OnLabel").toLocaleLowerCase()}
                ${moment(comment.CreateDate).format(dateFormat)}
                ${getText("AtLabel")}
                ${moment(comment.CreateDate).format("HH:mm:ss")}`;
    };

    const getCommentBody = (comment: Readonly<Comment>): string => {
        switch (comment.ActionType) {
            case CommentActionType.SyncToPortal:
                return getText("ConsultationRequestAction_SyncToPortal");

            case CommentActionType.Fax:
                return getText("ConsultationRequestAction_RequestFaxed");

            case CommentActionType.Print:
                return getText("ConsultationRequestAction_RequestPrinted");

            case CommentActionType.RevokeSignature:
                return getText("ConsultationRequestAction_SignatureRevoked");

            case CommentActionType.MedicalRequestRecipientChange:
                return `${getText("ConsultationRequestAction_RecipientUpdated")}: ${comment.Value}`;

            case CommentActionType.MedicalRequestStatusChange:
                return `${getText("ConsultationRequestAction_StatusUpdated")}: ${cacheStore.GetLocalizedValueByLookupId(comment.ActionResultLookupId)}`;

            case CommentActionType.Sign:
            case CommentActionType.UserComment:
            default:
                return comment.Value;
        }
    };

    const showLabel = cacheStore.ClinicSettings.EnableConsultationRequestHistory && props.label;

    return (
        <>
            {showLabel && <span data-cy="requesthistory-label" className={styles.medicalRequestRowLabel}>{props.label}</span>}
            <div className={styles.commentsThread}>
                {props.comments.map((comment, idx) => {
                    const employeeAsPerson = cacheStore.GetCliniciansPersonsByContactFacetId(comment.EmployeeId);
                    const commentBy = employeeAsPerson?.NameWithPrefix ?? "";
                    const noBorderClass = (!props.showLastItemBorder && idx === props.comments.length - 1) ? " border-none" : "";

                    return (
                        <div key={comment.Id} className={`notes-collection-item ${noBorderClass}`}>
                            <div className="memo-header">
                                <strong>{`${commentBy} `}</strong>
                                <span>{getCommentTitle(comment)}</span>
                            </div>
                            <div className="note-container">
                                {getCommentBody(comment)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}

export { MedicalRequestCommentsDisplay };
